/* no-variant-wakeup.js (max guard for single-variant products)
   - Set [name="id"], update ?variant=
   - Emit multiple variant events
   - Unhide/enable CTAs
   - Remove "hide-until-selected" gates on no-options products
   - Guard for late scripts (up to 10s) and re-correct
*/
(function () {
  const GUARD_MS = 10000;

  const $ = (sel, root=document) => root.querySelector(sel);
  const $$ = (sel, root=document) => Array.from(root.querySelectorAll(sel));

  function getScope(node) {
    return node.closest('.shopify-section') || node.closest('.product') || document;
  }

  function getProductJSON(scope) {
    const s =
      scope.querySelector('script[type="application/json"][data-product]') ||
      scope.querySelector('script[type="application/json"][data-product-json]') ||
      scope.querySelector('variant-selects script[type="application/json"]');
    if (!s || !s.textContent) return null;
    try { return JSON.parse(s.textContent); } catch { return null; }
  }

  function hasOptionPickers(scope) {
    return !!scope.querySelector('variant-selects, [data-variant-selects], .variant-selects');
  }

  function sectionId(scope) {
    const sec = scope.closest('.shopify-section');
    return (sec?.id || '').replace('shopify-section-','') || '';
  }

  function setHiddenId(scope, id) {
    $$('form[action*="/cart/add"] input[name="id"]', scope).forEach(inp => {
      inp.disabled = false;
      if (String(inp.value) !== String(id)) {
        inp.value = id;
        inp.dispatchEvent(new Event('change', { bubbles:true }));
      }
      // helpful for some apps
      inp.dataset.variantId = String(id);
    });
    const pf = $('product-form, form[action*="/cart/add"]', scope);
    if (pf) {
      pf.dataset.variantReady = 'true';
      pf.dataset.variantId = String(id);
      pf.classList.add('has-variant');
    }
  }

  function ensureUrlVariant(id) {
    try {
      const u = new URL(location.href);
      if (!u.searchParams.has('variant')) {
        u.searchParams.set('variant', id);
        history.replaceState({}, '', u.toString());
      }
    } catch {}
  }

  function emitVariantEvents(scope, variant) {
    const secId = sectionId(scope);
    const payload = { sectionId: secId, variant };
    ['variant-change', 'variant:change', 'variant:changed', 'variantUpdated'].forEach(name => {
      const ev = new CustomEvent(name, { bubbles:true, detail: payload });
      document.dispatchEvent(ev);
      const secEl = scope.closest('.shopify-section');
      if (secEl) secEl.dispatchEvent(ev);
    });
    document.documentElement.classList.add('variant-ready'); // CSS signal
  }

  function showEnableCTA(scope, variant) {
    const buttons = new Set();
    const submit = $('.product-form__submit, [type="submit"]', scope);
    if (submit) buttons.add(submit);
    const sticky = document.querySelector('.sticky-cta .button');
    if (sticky) buttons.add(sticky);

    // Unhide common wrappers
    const wrappers = new Set([
      $('.product-form__buttons', scope),
      $('.product-form__controls', scope),
      $('.product-form__footer', scope),
      $('.product-form', scope),
      $('[data-hide-actions-until-selected]', scope)
    ].filter(Boolean));

    // Remove "hide until selected" gates on single-variant pages
    wrappers.forEach(w => {
      w.removeAttribute('hidden');
      w.classList.remove('hidden', 'invisible', 'is-hidden', 'no-js-hidden');
      w.style.removeProperty('display');
      w.style.removeProperty('visibility');
      w.style.removeProperty('opacity');
      w.setAttribute('data-variant-ready', 'true');
      w.dataset.hideActionsUntilSelected = 'false';
    });

    // Toggle the buttons
    buttons.forEach(btn => {
      const parents = [btn, btn.closest('.product-form'), btn.closest('.product-form__buttons')].filter(Boolean);
      parents.forEach(el => {
        el.removeAttribute('hidden');
        el.classList.remove('hidden', 'invisible', 'is-hidden', 'no-js-hidden');
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
        el.style.removeProperty('opacity');
        el.removeAttribute('aria-hidden');
      });

      if (variant?.available !== false) {
        btn.removeAttribute('disabled');
        if (btn.getAttribute('aria-disabled') === 'true') btn.setAttribute('aria-disabled', 'false');
        btn.classList.remove('disabled');
      }
    });
  }

  function guard(scope, variant) {
    const targets = [
      $('.product-form__buttons', scope),
      $('.product-form__controls', scope),
      $('.product-form__footer', scope),
      $('.product-form', scope),
      $('.product', scope),
      document.querySelector('.sticky-cta')
    ].filter(Boolean);

    const fix = () => showEnableCTA(scope, variant);

    const obs = new MutationObserver(() => fix());
    targets.forEach(t => obs.observe(t, {
      attributes: true,
      attributeFilter: ['class','hidden','style','aria-hidden','data-hide-actions-until-selected','data-variant-ready'],
      subtree: false
    }));

    // also guard for sections being re-rendered / nodes replaced
    const bodyObs = new MutationObserver(muts => {
      let replaced = false;
      muts.forEach(m => {
        if (m.type === 'childList') {
          m.addedNodes.forEach(n => {
            if (!(n instanceof Element)) return;
            if (n.matches && (n.matches('product-form') || n.querySelector?.('form[action*="/cart/add"]'))) replaced = true;
          });
        }
      });
      if (replaced) fix();
    });
    bodyObs.observe(document.body, { childList:true, subtree:true });

    setTimeout(() => { obs.disconnect(); bodyObs.disconnect(); }, GUARD_MS);
  }

  function wake(scope) {
    if (hasOptionPickers(scope)) return; // not our case

    const product = getProductJSON(scope);
    if (!product || !Array.isArray(product.variants) || product.variants.length === 0) return;

    const variant = product.variants.find(v => v.available) || product.variants[0];
    if (!variant) return;

    setHiddenId(scope, variant.id);
    ensureUrlVariant(variant.id);
    emitVariantEvents(scope, variant);
    showEnableCTA(scope, variant);
    guard(scope, variant);
  }

  function start() {
    document.querySelectorAll('product-form, form[action*="/cart/add"]').forEach(pf => wake(getScope(pf)));
  }

  if (document.readyState === 'complete') setTimeout(start, 150);
  else window.addEventListener('load', () => setTimeout(start, 150), { once:true });
})();
